import { Device } from '@twilio/voice-sdk'
import axios from 'axios'
import { useEffect, useState } from 'react'
import { useAuth } from 'utils/auth-context'
import { useTwilioCallConnection, useTwilioDevice } from '../contact-center.hooks'
import { createInaudibleBuffer, playInaudibleSound } from '../contact-center.utils'

type IncomingCallProps = {
  onIncoming: () => void
  onDisconnect: () => void
  onCancel: () => void
}

export const useIncomingCall = ({ onIncoming, onDisconnect, onCancel }: IncomingCallProps) => {
  const { setDevice, device } = useTwilioDevice()
  const { setConnection } = useTwilioCallConnection()
  const [isAudioContextAvailable, setIsAudioContextAvailable] = useState(false)
  const { data } = useAuth()
  const account = data?.user

  useEffect(() => {
    if (!account || device) return
    ;(async () => {
      const url = `${process.env.REACT_APP_TWILIO_MICROSERVICE_URL}access-token`

      const identity = process.env.REACT_APP_TWILIO_IDENTITY_CLIENT
      /*const response = await axios.post<{ token: string }>(url, {
        identity: account?.login
      })*/

      const response = await axios.post<{ token: string }>(url, {
        identity: identity,
      })

      const token = response.data.token

      const device = new Device(token, {
        appName: process.env.REACT_APP_TWILIO_CLIENT_NAME,
        closeProtection: true,
        allowIncomingWhileBusy: !!process.env.REACT_APP_TWILIO_ALLOW_INCOMING_WHILE_BUSY,
        maxAverageBitrate: 40000,
      })

      device.register()

      device.addListener('registered', () =>
        console.log(`The device (${identity}) is ready to receive incoming calls.`),
      )

      device.on('tokenWillExpire', async () => {
        console.log('updating expired token')
        const response = await axios.post<{ token: string }>(url, {
          identity: identity,
        })
        const token = response.data.token
        device.updateToken(token)
      })

      device.on('incoming', connection => {
        setConnection(connection)

        onIncoming()

        console.log(`Incoming call from: ${connection.parameters.From}`)

        connection.on('accept', () => {
          console.log('Call accepted')
        })

        connection.on('disconnect', () => {
          console.log('Call disconnected')
          onDisconnect()
        })

        connection.on('cancel', () => {
          console.log('call is cancelled')
          onCancel()
        })
      })

      setDevice(device)

      device.on('ready', () => {
        console.log('Twilio Device is ready and active')
      })
    })()

    return () => {}
  }, [account])

  useEffect(() => {
    const clickEvent = document.addEventListener('click', () => {
      if (!isAudioContextAvailable) setIsAudioContextAvailable(true)
    }) as any
    return () => {
      document.removeEventListener('click', clickEvent)
    }
  }, [isAudioContextAvailable])
  useEffect(() => {
    if (!isAudioContextAvailable) return
    const AudioContext = window.AudioContext
    let event: any = null
    if (AudioContext) {
      const audioContext = new AudioContext()
      const iterations = 10000
      const interval = 5000
      const inaudibleBuffer = createInaudibleBuffer(audioContext)
      playInaudibleSound(audioContext, inaudibleBuffer, iterations, interval)
      audioContext.suspend()

      event = document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          audioContext.suspend()
        } else {
          audioContext.resume()
        }
      })
    } else {
      console.warn('Web Audio API is not supported in this browser or the tab is already visible.')
    }

    return () => {
      document.removeEventListener('visibilitychange', event)
    }
  }, [isAudioContextAvailable])
}
